import { RiQuestionAnswerLine } from '@react-icons/all-files/ri/RiQuestionAnswerLine'
import { ReactNode } from 'react'
import { ApacheCamelIcon } from '~/components/icons'
import { TaskParams } from '~/components/task-forms/BaseTaskForm'
import CodeReview from '~/docs/CodeReview.md'
import GenerateAddFlowCodePR from '~/docs/GenerateAddFlowCodePR.md'
import GenerateDataweave from '~/docs/GenerateDataweave.md'
import DataweaveToDataCreation from '~/docs/GenerateDataweaveToDataCreation.md'
import GenerateDocumentation from '~/docs/GenerateDocumentation.md'
import GenerateFullRepoTechManifestDocumentation from '~/docs/GenerateFullRepoTechManifestDocumentation.md'
import GenerateImage from '~/docs/GenerateImage.md'
import GenerateMUnitTestsForFlow from '~/docs/GenerateMUnitTestsForFlow.md'
import GenerateMappingTable from '~/docs/GenerateMappingTable.md'
import GenerateMultiRepoFlowDiagramFlows from '~/docs/GenerateMultiRepoFlowDiagramFlows.md'
import MdInputOutputToDWL from '~/docs/InputOutputToDWL.md'
import MigrationAccelerator from '~/docs/MigrationAccelerator.md'
import SpringIntegrationRefactor from '~/docs/SpringIntegrationRefactor.md'
import MulesoftToCamel from '~/docs/MulesoftToCamel.md'
import MulesoftToNodeJS from '~/docs/MulesoftToNodeJS.md'
import MultiRepoQuestionAnswering from '~/docs/MultiRepoQuestionAnswering.md'
import MultiRepoQuestionAnsweringDebug from '~/docs/MultiRepoQuestionAnsweringDebug.md'
import QuestionAnswering from '~/docs/QuestionAnswering.md'
import MdSimpleCodingTask from '~/docs/SimpleCodingTask.md'
import { AgentTaskStatus, CommandType, sourceOutputExampleTypeToJSON } from '~/protos/agents'
import {
  ChatIcon,
  CodeIcon,
  DiagramIcon,
  DocumentIcon,
  MigrationIcon,
  NodeJsIcon,
  RepositoryIcon,
  TableIcon,
  TestIcon
} from './icon'

export type UsedCommandType = Exclude<
  CommandType,
  CommandType.NONE | CommandType.UNRECOGNIZED | CommandType.FIX_PR | CommandType.MEGA_QA_AGENT_V1
>

export enum RequiredField {
  MULTI_REPO = 'multiRepo',
  REPO = 'repo',
  BRANCH = 'branch',
  DESCRIPTION = 'description',
  FLOW = 'flow',
  API = 'api',
  MAPPING_TABLE = 'mappingTable',
  DATAWEAVE = 'dataweave',
  INPUT_OUTPUT_EXAMPLES = 'inputOutputExamples'
}

export interface AgentCommand {
  id: UsedCommandType
  label: string
  desc: string
  icon: ReactNode
  doc: string
  requiredFields: RequiredField[]
  steps: AgentTaskStatus[]
  codeContextGroup?: CodeContextGroup
  codeContextOption?: CodeContextOption[]
  hasOptions?: boolean // Whether to show the three-dot menu with "Try example" and "Start my own" options
  hasExample?: boolean // Whether "Try example" is available (defaults to true if not specified)
}
export type TypeAgentCommands = Partial<Record<UsedCommandType, AgentCommand>>
export enum CodeContextGroup {
  DATAWEAVE = 'DATAWEAVE',
  INTEGRATION = 'INTEGRATION',
  SAMPLE_DATA = 'SAMPLE_DATA',
  CODE_ENHANCER = 'CODE_ENHANCER',
  STANDALONE = 'STANDALONE_'
}

export enum CodeContextOption {
  NO_REPOSITORY = 'NO_REPOSITORY',
  WITH_REPOSITORY = 'WITH_REPOSITORY',
  UPLOAD_FROM_COMPUTER = 'UPLOAD_FROM_COMPUTER',
  WITH_MAPPING_TABLE = 'WITH_MAPPING_TABLE'
}

export const getCommandCodeContextGroup = (
  command?: CommandType
): CodeContextGroup | string | undefined => {
  if (!command) return undefined
  const agentCommand = AgentCommands[command as UsedCommandType]

  if (agentCommand?.codeContextGroup) {
    return agentCommand.codeContextGroup
  }

  const label = agentCommand?.label ?? ''
  return `${CodeContextGroup.STANDALONE}${label.toUpperCase().replace(/\s+/g, '_')}`
}

const buildAgentCommands = () => {
  const ICON_SIZE = 24
  const agentCommands: Partial<Record<UsedCommandType, Omit<AgentCommand, 'id'>>> = {
    [CommandType.AUTODESK_MULE_TO_NODE_JS_MIGRATION]: {
      label: 'MuleSoft to Node.js',
      desc: 'Migrate MuleSoft Flow/API Endpoint to NodeJS',
      doc: MulesoftToNodeJS,
      icon: <NodeJsIcon />,
      requiredFields: [RequiredField.REPO],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED
      ],
      hasOptions: true
    },
    [CommandType.MULESOFT_TO_CAMEL_MIGRATION]: {
      label: 'MuleSoft to Camel',
      desc: 'Migrate MuleSoft Flow/API Endpoint to Camel',
      doc: MulesoftToCamel,
      icon: <ApacheCamelIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      hasOptions: false
    },
    [CommandType.MIGRATION_DOCUMENT]: {
      label: 'Migration Generator',
      desc: 'Generate migration documents',
      doc: MulesoftToNodeJS,
      icon: <MigrationIcon />,
      requiredFields: [RequiredField.REPO],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.DOCUMENT_GENERATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: false
    },
    [CommandType.BILLABLE_FLOW_DOCUMENTATION]: {
      label: 'Migration Inventory',
      desc: 'Generate API inventory for Migrations',
      doc: MulesoftToNodeJS,
      icon: <MigrationIcon />,
      requiredFields: [RequiredField.REPO],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.DOCUMENT_GENERATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: false
    },
    [CommandType.MIGRATION_ACCELERATOR]: {
      label: 'Migration Accelerator',
      desc: 'Accelerate migration with comprehensive analysis',
      doc: MigrationAccelerator,
      icon: <MigrationIcon />,
      requiredFields: [RequiredField.MULTI_REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED,
        AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED,
        AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED,
        AgentTaskStatus.MIGRATION_API_SPEC_GENERATED,
        AgentTaskStatus.MIGRATION_CODE_GENERATED,
        AgentTaskStatus.MIGRATION_REFACTOR_ACCELERATOR_DIFF_GENERATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: false
    },
    [CommandType.AZURE_DEPLOY_TEST]: {
      label: 'Azure Migrate + Deploy',
      desc: 'Migrate to Azure and deploy (deploy currently simulated)',
      doc: MigrationAccelerator,
      icon: <MigrationIcon />,
      requiredFields: [],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.MIGRATION_AWAITING_DEPLOY_INPUT,
        AgentTaskStatus.MIGRATION_DEPLOY_INPUT_PROVIDED,
        AgentTaskStatus.MIGRATION_DEPLOYING,
        AgentTaskStatus.MIGRATION_DEPLOYED
      ],
      hasOptions: false
    },
    [CommandType.MULE_REFACTORING]: {
      label: 'Mule Refactor Assistant',
      desc: 'Refactor legacy Mule integrations into API-led architecture',
      doc: MigrationAccelerator,
      icon: <MigrationIcon />,
      requiredFields: [RequiredField.MULTI_REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED,
        AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED,
        AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED,
        AgentTaskStatus.MIGRATION_API_SPEC_GENERATED,
        AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED,
        AgentTaskStatus.REFACTOR_USER_NOTES_ADDED,
        AgentTaskStatus.REFACTOR_API_SPECS_GENERATED,
        AgentTaskStatus.REFACTOR_CODE_GENERATED,
        AgentTaskStatus.MIGRATION_REFACTOR_ACCELERATOR_DIFF_GENERATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: false
    },
    [CommandType.SPRING_INTEGRATION_REFACTORING]: {
      label: 'Spring Integration Refactor',
      desc: 'Refactor an existing integration repository into Spring Boot 3.x + Spring Integration',
      doc: SpringIntegrationRefactor,
      icon: <MigrationIcon />,
      requiredFields: [RequiredField.MULTI_REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.MIGRATION_ENDPOINTS_IDENTIFIED,
        AgentTaskStatus.MIGRATION_ENDPOINTS_SELECTED,
        AgentTaskStatus.MIGRATION_DOCUMENT_GENERATED,
        AgentTaskStatus.MIGRATION_API_SPEC_GENERATED,
        AgentTaskStatus.REFACTOR_EXISTING_DOCS_GENERATED,
        AgentTaskStatus.REFACTOR_USER_NOTES_ADDED,
        AgentTaskStatus.REFACTOR_API_SPECS_GENERATED,
        AgentTaskStatus.REFACTOR_CODE_GENERATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: false
    },
    [CommandType.QUESTION_ANSWERING]: {
      label: 'Single Repo Code Lens',
      desc: 'Ask question for a single repository',
      doc: QuestionAnswering,
      icon: <ChatIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      hasOptions: true
    },
    [CommandType.MIGRATION_QUESTION_ANSWERING]: {
      label: 'Migration Repo Code Lens',
      desc: 'Ask migration questions for a repository',
      doc: QuestionAnswering,
      icon: <ChatIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      hasOptions: false
    },
    [CommandType.MULTI_REPO_QA]: {
      label: 'Multi Repo Code Lens',
      desc: 'Ask question for multiple repositories',
      doc: MultiRepoQuestionAnswering,
      icon: <ChatIcon />,
      requiredFields: [RequiredField.MULTI_REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      hasOptions: false
    },
    [CommandType.CODE_REVIEW]: {
      label: 'Code Review Lens',
      desc: 'Review a pull request for the provided repository',
      doc: CodeReview,
      icon: <CodeIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED
      ],
      hasOptions: false
    },
    [CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM]: {
      label: 'Domain Diagram Generator',
      desc: 'Generate diagram for multiple APIs',
      doc: GenerateMultiRepoFlowDiagramFlows,
      icon: <DiagramIcon />,
      requiredFields: [RequiredField.MULTI_REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      hasOptions: false
    },
    [CommandType.GENERAL_MULTI_REPO_QA_DEBUG_AGENT]: {
      label: 'Multi-Repo Q&A (Debug)',
      desc: 'Ask questions for multiple repositories',
      doc: MultiRepoQuestionAnsweringDebug,
      icon: <RiQuestionAnswerLine size={ICON_SIZE} />,
      requiredFields: [RequiredField.MULTI_REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      hasOptions: true
    },
    [CommandType.GENERATE_IMAGE]: {
      label: 'Diagram Generator',
      desc: 'Generate a sequence & flow diagram/s',
      doc: GenerateImage,
      icon: <DiagramIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: true
    },
    [CommandType.GENERATE_SIMPLE_CODE_PR]: {
      label: 'Repository Coder',
      desc: 'Generate or update flows, dataweaves or munits',
      doc: MdSimpleCodingTask,
      icon: <CodeIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.AGENT_INTERNAL_STATUS,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      codeContextGroup: CodeContextGroup.CODE_ENHANCER,
      codeContextOption: [
        CodeContextOption.NO_REPOSITORY,
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: true
    },
    [CommandType.GENERATE_DOCUMENTATION]: {
      label: 'Document Generator',
      desc: 'Generate documentation for flows, endpoints',
      doc: GenerateDocumentation,
      icon: <DocumentIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: true
    },
    [CommandType.MULE_3_TO_MULE_4_MIGRATION]: {
      label: 'Mule 3 to Mule 4 Migration',
      desc: 'Migrate Mule 3 flow to Mule 4',
      doc: GenerateDocumentation,
      icon: <DocumentIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: false
    },
    [CommandType.GENERATE_FULL_REPO_TECH_MANIFEST_DOCUMENTATION]: {
      label: 'Repository Docs Generator',
      desc: 'Generate docs for a repository',
      doc: GenerateFullRepoTechManifestDocumentation,
      icon: <RepositoryIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      hasOptions: true
    },
    [CommandType.GENERATE_MAPPING_TABLE]: {
      label: 'Mapping Table Generator',
      desc: 'Generate mapping table from dataweave script',
      doc: GenerateMappingTable,
      icon: <TableIcon />,
      requiredFields: [RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.DOCUMENT_GENERATED
      ],
      hasOptions: true
    },
    [CommandType.GENERATE_ADD_FLOW_CODE_PR]: {
      label: 'Integration Generator',
      desc: 'Create flow code from flow specifications and flow diagrams',
      doc: GenerateAddFlowCodePR,
      icon: <CodeIcon />,
      requiredFields: [RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.AGENT_INTERNAL_STATUS,
        AgentTaskStatus.DOCUMENT_GENERATED,
        AgentTaskStatus.DOCUMENT_UPDATED
      ],
      codeContextGroup: CodeContextGroup.INTEGRATION,
      codeContextOption: [CodeContextOption.NO_REPOSITORY],
      hasOptions: true
    },
    [CommandType.GENERATE_REPO_ADD_FLOW_CODE_PR]: {
      label: 'Integration Generator',
      desc: 'Create flow code with repository awareness',
      doc: GenerateAddFlowCodePR,
      icon: <CodeIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.AGENT_INTERNAL_STATUS,
        AgentTaskStatus.DOCUMENT_GENERATED,
        AgentTaskStatus.DOCUMENT_UPDATED
      ],
      codeContextGroup: CodeContextGroup.INTEGRATION,
      codeContextOption: [CodeContextOption.WITH_REPOSITORY, CodeContextOption.UPLOAD_FROM_COMPUTER]
    },
    [CommandType.DATAWEAVE_TO_DATA_CREATION]: {
      label: 'Sample Data Generator',
      desc: 'Sample data from dataweave',
      doc: DataweaveToDataCreation,
      icon: <TestIcon />,
      requiredFields: [RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED
      ],
      codeContextGroup: CodeContextGroup.SAMPLE_DATA,
      codeContextOption: [CodeContextOption.NO_REPOSITORY],
      hasOptions: true
    },
    [CommandType.DATAWEAVE_TO_DATA_CREATION_REPO_AWARE]: {
      label: 'Sample Generator',
      desc: 'Sample data from dataweave with repository awareness',
      doc: DataweaveToDataCreation,
      icon: <TestIcon />,
      requiredFields: [RequiredField.DESCRIPTION, RequiredField.REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED
      ],
      codeContextGroup: CodeContextGroup.SAMPLE_DATA,
      codeContextOption: [CodeContextOption.WITH_REPOSITORY, CodeContextOption.UPLOAD_FROM_COMPUTER]
    },
    [CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION]: {
      label: 'DataWeave Generator',
      desc: 'From input output examples or mapping table',
      doc: MdInputOutputToDWL,
      icon: <CodeIcon />,
      requiredFields: [RequiredField.INPUT_OUTPUT_EXAMPLES],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED
      ],
      codeContextGroup: CodeContextGroup.DATAWEAVE,
      codeContextOption: [CodeContextOption.NO_REPOSITORY],
      hasOptions: true
    },
    [CommandType.INPUT_OUTPUT_EXAMPLE_TO_DWL_GENERATION_REPO_AWARE]: {
      label: 'DataWeave Generator',
      desc: 'from input output examples with repository awareness',
      doc: MdInputOutputToDWL,
      icon: <CodeIcon />,
      requiredFields: [
        RequiredField.REPO,
        RequiredField.BRANCH,
        RequiredField.INPUT_OUTPUT_EXAMPLES
      ],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED
      ],
      codeContextGroup: CodeContextGroup.DATAWEAVE,
      codeContextOption: [CodeContextOption.WITH_REPOSITORY, CodeContextOption.UPLOAD_FROM_COMPUTER]
    },
    [CommandType.GENERATE_DATAWEAVE]: {
      label: 'DataWeave Generator',
      desc: 'from mapping table',
      doc: GenerateDataweave,
      icon: <CodeIcon />,
      requiredFields: [RequiredField.MAPPING_TABLE],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED
      ],
      codeContextGroup: CodeContextGroup.DATAWEAVE,
      codeContextOption: [CodeContextOption.WITH_MAPPING_TABLE]
    },
    [CommandType.GENERATE_DATAWEAVE_REPO_AWARE]: {
      label: 'DataWeave Generator 4',
      desc: 'from mapping table with repository awareness',
      doc: GenerateDataweave,
      icon: <CodeIcon />,
      requiredFields: [RequiredField.MAPPING_TABLE, RequiredField.REPO, RequiredField.BRANCH],
      steps: [AgentTaskStatus.SUBMITTED, AgentTaskStatus.IN_PROGRESS, AgentTaskStatus.COMPLETED]
    },
    [CommandType.MUNIT_TEST_CREATION]: {
      label: 'MUnit Test Generator',
      desc: 'Generate MUnit tests for a flow',
      doc: GenerateMUnitTestsForFlow,
      icon: <TestIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED,
        AgentTaskStatus.MUNIT_DIFF_GENERATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: true
    },
    [CommandType.INTEGRATION_TEST]: {
      label: 'Integration Test Agent',
      desc: 'Run comprehensive integration tests for MuleSoft flows',
      doc: GenerateMUnitTestsForFlow,
      icon: <TestIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED,
        AgentTaskStatus.INTEGRATION_TEST_DIFF_GENERATED
      ],
      hasOptions: false
    },
    [CommandType.MULE_VERSION_UPGRADE]: {
      label: 'Mule Version Upgrades',
      desc: 'Upgrade Mule applications to newer runtime versions',
      doc: MigrationAccelerator,
      icon: <MigrationIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.AGENT_INTERNAL_STATUS,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      codeContextGroup: CodeContextGroup.CODE_ENHANCER,
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: false
    },
    [CommandType.CODE_REVIEW_API]: {
      label: 'Code Review API',
      desc: 'Automated code review via API',
      doc: CodeReview,
      icon: <CodeIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.COMPLETED
      ],
      hasOptions: false
    },
    [CommandType.NON_MULE_DOCUMENTATION]: {
      label: 'Non-Mule Documentation',
      desc: 'Generate documentation for non-MuleSoft projects',
      doc: GenerateDocumentation,
      icon: <DocumentIcon />,
      requiredFields: [RequiredField.REPO, RequiredField.BRANCH, RequiredField.DESCRIPTION],
      steps: [
        AgentTaskStatus.SUBMITTED,
        AgentTaskStatus.IN_PROGRESS,
        AgentTaskStatus.PLANNING_COMPLETED,
        AgentTaskStatus.PLANNING_UPDATED,
        AgentTaskStatus.DIFF_GENERATED,
        AgentTaskStatus.DIFF_COMMENT_ADDED,
        AgentTaskStatus.HUMAN_DIFF_UPDATED
      ],
      codeContextOption: [
        CodeContextOption.WITH_REPOSITORY,
        CodeContextOption.UPLOAD_FROM_COMPUTER
      ],
      hasOptions: false
    }
  }
  return Object.entries(agentCommands).reduce((acc: TypeAgentCommands, [key, value]) => {
    const id = Number(key) as AgentCommand['id']
    acc[id] = { ...value, id: Number(key) as AgentCommand['id'] }
    return acc
  }, {} as TypeAgentCommands)
}

export const AgentCommands = buildAgentCommands()
export const QuestionAnsweringCommands = [
  CommandType.QUESTION_ANSWERING,
  CommandType.MIGRATION_QUESTION_ANSWERING,
  CommandType.MULTI_REPO_QA,
  CommandType.CODE_REVIEW
]
export const FixedPromptCommands = [
  CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM,
  CommandType.GENERATE_FULL_REPO_TECH_MANIFEST_DOCUMENTATION,
  CommandType.GENERAL_MULTI_REPO_QA_DEBUG_AGENT
]

const buildFormCommands = () => {
  const commands = Object.values(AgentCommands).filter(({ id }) => {
    if (QuestionAnsweringCommands.concat(FixedPromptCommands).includes(id)) {
      return false
    }
    return true
  })
  return commands.map(({ id }) => id)
}

export const FormCommands = buildFormCommands()

export const isNoTryExampleCommand = (command?: CommandType) => {
  return [
    CommandType.GENERATE_MULTI_REPO_FLOW_DIAGRAM,
    CommandType.MULTI_REPO_QA,
    CommandType.MULESOFT_TO_CAMEL_MIGRATION,
    CommandType.MIGRATION_DOCUMENT,
    CommandType.MIGRATION_QUESTION_ANSWERING,
    CommandType.CODE_REVIEW,
    CommandType.MULE_3_TO_MULE_4_MIGRATION,
    CommandType.BILLABLE_FLOW_DOCUMENTATION,
    CommandType.MIGRATION_ACCELERATOR,
    CommandType.AZURE_DEPLOY_TEST,
    CommandType.MULE_REFACTORING,
    CommandType.SPRING_INTEGRATION_REFACTORING,
    CommandType.INTEGRATION_TEST,
    CommandType.MULE_VERSION_UPGRADE,
    CommandType.CODE_REVIEW_API,
    CommandType.NON_MULE_DOCUMENTATION
  ].includes(command as UsedCommandType)
}

export const isRepoRequired = (command?: CommandType) => {
  return isFieldRequired(RequiredField.REPO, command)
}

export const isMultiRepoRequired = (command?: CommandType) => {
  return isFieldRequired(RequiredField.MULTI_REPO, command)
}

export const isFieldRequired = (requiredField: RequiredField, command?: CommandType) => {
  if (!command) return false
  const filter = ({ requiredFields }: AgentCommand) => requiredFields.includes(requiredField)
  const requiredTasks = Object.values(AgentCommands).filter(filter)
  return requiredTasks.map(({ id }) => id).includes(command as UsedCommandType)
}

export const isRepoNotRequired = (command?: CommandType) => {
  return !isRepoRequired(command)
}

export const isBranchRequired = (command?: CommandType) => {
  return isFieldRequired(RequiredField.BRANCH, command)
}

export const isBranchNotRequired = (command?: CommandType) => {
  return !isBranchRequired(command)
}

export const isTaskCommand = (command?: UsedCommandType) => {
  return !QuestionAnsweringCommands.includes(command as UsedCommandType)
}
export const isChatCommand = (command?: UsedCommandType) => {
  return !isTaskCommand(command)
}

export const isInlineChatCommand = (command?: UsedCommandType) => {
  return isTaskCommand(command) && isRepoRequired(command)
}

export const isEitherRepoBranchNotRequired = (command: CommandType) => {
  return isRepoNotRequired(command) && isBranchNotRequired(command)
}

export const isRepoBranchNotSatisfied = (
  repos: { index_name: string }[],
  command?: CommandType,
  repository?: string,
  branch?: string
) => {
  const areReposEmpty = Object.keys(repos).length === 0
  const isRepoNotSatisfied = isRepoRequired(command) && (areReposEmpty || !repository)
  const isBranchNotSatisfied = isBranchRequired(command) && !branch
  return isRepoNotSatisfied || isBranchNotSatisfied
}

export const isCodeContextNotSatisfied = (
  currentOption: 'upload' | 'codeContext' | 'noRepo',
  uploadedPath: string,
  repos: { index_name: string }[],
  repository?: string,
  branch?: string,
  command?: CommandType
) => {
  if (currentOption === 'upload') {
    const isUploadNotSatisfied = uploadedPath == null || uploadedPath === ''
    return isRepoRequired(command) && isUploadNotSatisfied
  }
  if (currentOption === 'codeContext') {
    return isRepoBranchNotSatisfied(repos, command, repository, branch)
  }
  if (currentOption === 'noRepo') {
    return false // No repo option means no repo/branch validation needed
  }
  return false
}

export const areRequiredFieldsNotSatisfied = (command?: CommandType, task?: TaskParams | null) => {
  return getRequiredFieldsErrorMessage(command, task) !== undefined
}

/**
 * Returns a user-facing message describing the first missing required field
 * for a task form, or undefined if all required fields are satisfied. Drives
 * the toast that fires when submit is attempted on an incomplete form so the
 * user knows exactly what to fix instead of seeing a generic "Form is incomplete".
 */
export const getRequiredFieldsErrorMessage = (
  command?: CommandType,
  task?: TaskParams | null
): string | undefined => {
  const description = task?.request?.command?.commandString || ''
  const descriptionTrimmed = description.replace(/\s/g, '')
  if (isFieldRequired(RequiredField.DESCRIPTION, command) && (!task || descriptionTrimmed === '')) {
    return 'Please enter a description before submitting.'
  }
  if (
    isFieldRequired(RequiredField.MAPPING_TABLE, command) &&
    (!task || !task.request?.mappingSheet?.length)
  ) {
    return 'Please provide a mapping table before submitting.'
  }
  if (isFieldRequired(RequiredField.INPUT_OUTPUT_EXAMPLES, command)) {
    if (!task || !task.request?.sourceOutputExamples?.[0]?.sourceData) {
      return 'Please provide at least one input/output example before submitting.'
    }
    const sourceExample = task.request?.sourceOutputExamples?.[0]
    if (
      sourceExample?.sourceDataType !== undefined &&
      sourceOutputExampleTypeToJSON(sourceExample.sourceDataType) === 'DATA_TYPE_EDIFACT'
    ) {
      const schema = sourceExample.inputEdiFactSchemaType
      const versionMissing = !schema?.version || schema.version.trim() === ''
      const messageMissing = !schema?.message || schema.message.trim() === ''
      if (!schema || (versionMissing && messageMissing)) {
        return 'Please complete the EDIFACT schema (version and message) before submitting.'
      }
      if (versionMissing) {
        return 'Please enter the EDIFACT schema version before submitting.'
      }
      if (messageMissing) {
        return 'Please enter the EDIFACT schema message before submitting.'
      }
    }
  }
  return undefined
}

export { CommandType }
